import React, { useState, useEffect } from 'react';
import orderService from '../services/orderService';
import facilityService from '../services/facilityService';
import InvoiceDownloadButton from '../components/InvoiceDownloadButton';
import './OrdersPage.css';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    facility_id: '',
    status: '',
    date: ''
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [emailModalOrder, setEmailModalOrder] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadOrders();
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Load facilities first
      const facilitiesData = await facilityService.getAllFacilities();
      setFacilities(facilitiesData);
      
      // Then load orders
      await loadOrders();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Fehler beim Laden der Daten: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      
      const ordersData = await orderService.getOrders(cleanFilters);
      setOrders(ordersData);
      setError('');
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('Fehler beim Laden der Aufträge: ' + error.message);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      facility_id: '',
      status: '',
      date: ''
    });
    setSearchTerm('');
  };

  // Filter orders by search term (client-side)
  const filteredOrders = orders.filter(order => {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase();
    const firstName = (order.customer?.first_name || '').toLowerCase();
    const lastName = (order.customer?.last_name || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`;
    
    return firstName.includes(search) || 
           lastName.includes(search) || 
           fullName.includes(search);
  });

  const handleOrderClick = async (order) => {
    try {
      // Load full order details including items and payments
      const fullOrder = await orderService.getOrder(order.id);
      setSelectedOrder(fullOrder);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error loading order details:', error);
      setError('Fehler beim Laden der Auftragsdetails: ' + error.message);
    }
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const openEmailModal = (order) => {
    const invoice = order.invoices && order.invoices.length > 0 ? order.invoices[0] : null;
    if (invoice) {
      setEmailModalOrder({ ...order, invoice });
      setEmailAddress(order.customer?.email || '');
      setEmailAddress(''); // Clear previous email
    } else {
      setError('Keine Rechnung für diesen Auftrag verfügbar');
    }
  };

  const closeEmailModal = () => {
    setEmailModalOrder(null);
    setEmailAddress('');
    setSendingEmail(false);
  };

  const handleResendEmail = async () => {
    if (!emailAddress.trim()) {
      setError('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    try {
      setSendingEmail(true);
      await orderService.resendInvoiceEmail(emailModalOrder.invoice.id, emailAddress);
      
      // Update the invoice email_sent status
      const updatedOrders = orders.map(order => {
        if (order.id === emailModalOrder.id) {
          return {
            ...order,
            invoices: order.invoices.map(inv => 
              inv.id === emailModalOrder.invoice.id 
                ? { ...inv, email_sent: true }
                : inv
            )
          };
        }
        return order;
      });
      setOrders(updatedOrders);

      closeEmailModal();
      setError('');
      // Show success message briefly
      const successMsg = 'Rechnung erfolgreich per E-Mail versendet';
      setError(''); // Clear any previous errors
      setTimeout(() => {
        // You could show a success toast here instead
        console.log(successMsg);
      }, 100);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Fehler beim Senden der E-Mail: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const getOrderTotal = (order) => {
    if (!order.order_items) return 0;
    return order.order_items.reduce((sum, item) => sum + item.total_price, 0);
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <header className="page-header">
        <h1>Auftragsübersicht</h1>
        <p>Verwalten Sie alle Aufträge und Rechnungen</p>
      </header>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label htmlFor="facility-filter">Anlage:</label>
            <select 
              id="facility-filter"
              value={filters.facility_id} 
              onChange={(e) => handleFilterChange('facility_id', e.target.value)}
            >
              <option value="">Alle Anlagen</option>
              {facilities.map(facility => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="status-filter">Status:</label>
            <select 
              id="status-filter"
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Alle Status</option>
              <option value="draft">Entwurf</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="completed">Abgeschlossen</option>
              <option value="cancelled">Storniert</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="date-filter">Datum:</label>
            <input 
              id="date-filter"
              type="date" 
              value={filters.date} 
              onChange={(e) => handleFilterChange('date', e.target.value)}
            />
          </div>

          <div className="filter-group search-group">
            <label htmlFor="search-filter">Suche:</label>
            <input 
              id="search-filter"
              type="text"
              placeholder="Kundenname..."
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="clear-filters-btn" onClick={clearFilters}>
            Filter zurücksetzen
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Auftrag #</th>
              <th>Kunde</th>
              <th>Anlage</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th>Gesamt</th>
              <th>Rechnung</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {searchTerm ? 'Keine Aufträge für diese Suche gefunden' : 'Keine Aufträge gefunden'}
                </td>
              </tr>
            ) : (
              filteredOrders.map(order => {
                const invoice = order.invoices && order.invoices.length > 0 ? order.invoices[0] : null;
                return (
                  <tr key={order.id} onClick={() => handleOrderClick(order)} className="clickable-row">
                    <td>#{order.id}</td>
                    <td>
                      {order.customer ? 
                        `${order.customer.first_name} ${order.customer.last_name}` 
                        : 'Kein Kunde'
                      }
                    </td>
                    <td>{order.facility?.name || 'Unbekannt'}</td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: orderService.getStatusColor(order.status) }}
                      >
                        {orderService.getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td>{orderService.formatDateTime(order.created_at)}</td>
                    <td>{orderService.formatCurrency(getOrderTotal(order))}</td>
                    <td>
                      {invoice ? (
                        <div className="invoice-info">
                          <span className="invoice-number">{invoice.invoice_number}</span>
                          {invoice.email_sent && (
                            <span className="email-sent-indicator" title="Per E-Mail versendet">📧</span>
                          )}
                        </div>
                      ) : (
                        <span className="no-invoice">Keine Rechnung</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        {invoice && (
                          <>
                            {order.invoices.map(inv => (
                              <InvoiceDownloadButton key={inv.id} invoice={inv} onError={setError} />
                            ))}
                            <button
                              className="action-btn email-btn"
                              onClick={() => openEmailModal(order)}
                              title="Rechnung per E-Mail senden"
                            >
                              ✉️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Auftrag #{selectedOrder.id}</h2>
              <button className="close-btn" onClick={closeOrderModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="order-details">
                <div className="detail-section">
                  <h3>Kunde</h3>
                  {selectedOrder.customer ? (
                    <div>
                      <p><strong>Name:</strong> {selectedOrder.customer.first_name} {selectedOrder.customer.last_name}</p>
                      {selectedOrder.customer.email && <p><strong>E-Mail:</strong> {selectedOrder.customer.email}</p>}
                      {selectedOrder.customer.phone && <p><strong>Telefon:</strong> {selectedOrder.customer.phone}</p>}
                    </div>
                  ) : (
                    <p>Kein Kunde zugewiesen</p>
                  )}
                </div>

                <div className="detail-section">
                  <h3>Auftragsdetails</h3>
                  <p><strong>Status:</strong> {orderService.getStatusLabel(selectedOrder.status)}</p>
                  <p><strong>Anlage:</strong> {selectedOrder.facility?.name}</p>
                  <p><strong>Erstellt:</strong> {orderService.formatDateTime(selectedOrder.created_at)}</p>
                  {selectedOrder.start_time && <p><strong>Begonnen:</strong> {orderService.formatDateTime(selectedOrder.start_time)}</p>}
                  {selectedOrder.end_time && <p><strong>Abgeschlossen:</strong> {orderService.formatDateTime(selectedOrder.end_time)}</p>}
                  {selectedOrder.notes && <p><strong>Notizen:</strong> {selectedOrder.notes}</p>}
                </div>

                {selectedOrder.order_items && selectedOrder.order_items.length > 0 && (
                  <div className="detail-section">
                    <h3>Positionen</h3>
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>Produkt</th>
                          <th>Menge</th>
                          <th>Preis/Stück</th>
                          <th>Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.order_items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.packaging_type?.name || 'Unbekanntes Produkt'}</td>
                            <td>{item.quantity}</td>
                            <td>{orderService.formatCurrency(item.price_per_unit)}</td>
                            <td>{orderService.formatCurrency(item.total_price)}</td>
                          </tr>
                        ))}
                        <tr className="total-row">
                          <td colSpan="3"><strong>Gesamtsumme:</strong></td>
                          <td><strong>{orderService.formatCurrency(getOrderTotal(selectedOrder))}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedOrder.invoices && selectedOrder.invoices.length > 0 && (
                  <div className="detail-section">
                    <h3>Rechnungen</h3>
                    {selectedOrder.invoices.map((invoice, index) => (
                      <div key={index} className="invoice-item">
                        <p><strong>Rechnungsnummer:</strong> {invoice.invoice_number}</p>
                        <p><strong>Erstellt:</strong> {orderService.formatDateTime(invoice.created_at)}</p>
                        <p><strong>E-Mail versendet:</strong> {invoice.email_sent ? 'Ja' : 'Nein'}</p>
                        <div className="invoice-actions">
                          <InvoiceDownloadButton invoice={invoice} variant="full" onError={setError} />
                          <button
                            className="btn btn-secondary"
                            onClick={() => openEmailModal(selectedOrder)}
                          >
                            ✉️ Per E-Mail senden
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModalOrder && (
        <div className="modal-overlay" onClick={closeEmailModal}>
          <div className="modal-content email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rechnung per E-Mail senden</h2>
              <button className="close-btn" onClick={closeEmailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p><strong>Auftrag:</strong> #{emailModalOrder.id}</p>
              <p><strong>Rechnung:</strong> {emailModalOrder.invoice.invoice_number}</p>
              
              <div className="form-group">
                <label htmlFor="email-input">E-Mail-Adresse:</label>
                <input 
                  id="email-input"
                  type="email" 
                  value={emailAddress} 
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="kunde@example.com"
                  disabled={sendingEmail}
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleResendEmail}
                  disabled={sendingEmail || !emailAddress.trim()}
                >
                  {sendingEmail ? 'Wird gesendet...' : 'E-Mail senden'}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={closeEmailModal}
                  disabled={sendingEmail}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;